import { Alert, Button, Container, FormControl, IconButton, InputLabel, MenuItem, Select } from '@mui/material';
import { NavLink, useParams } from "react-router-dom";
import { useEffect, useState } from 'react';

import { IBatch, IBatchPayeeOption, IPinkSheetData, IShipmentLight, IShipmentManifestWarningSet, IShipmentMaterial } from "../../../types/shipment";
import { IDocument } from '../../../types/document';

import { ErrorProvider } from '../../../classes/errorProvider';
import { useUserContext } from "../../../contexts/userContext";
import { useListContext } from '../../../contexts/listContext';

import { BatchService } from '../../../services/batchService';
import { DocumentService } from '../../../services/documentService';
import { ShipmentService } from '../../../services/shipmentService';
import { UtilityService } from '../../../services/utilityService';

import PermissionGate from "../../permissionGate";
import Navigation from "../../header";
import { Icons } from '../../icons';
import Json from '../../json';
import Spinner from '../../spinner';

import BatchDetailsForm from '../../forms/batchDetailsForm';
import BatchHistoryForm from '../../forms/batchHistoryForm';
import BatchShipmentsForm from '../../forms/batchShipmentsForm';
import BatchWorkflowForm from '../../forms/batchWorkflowForm';
import DocumentTable from '../../tables/documentTable';
import DocumentViewer from '../../documentViewer';
import PowerBiReportViewer from '../../powerBiReportViewer';
import PinkSheet from '../../pinkSheet';

import '../../../styles/batchEdit.scss';

const BatchEdit = () => {
	const userContext = useUserContext();
	const listContext = useListContext();
	let { id } = useParams();
	const [entityTypeId, setEntityTypeId] = useState("");
	const [activeTab, setActiveTab] = useState("details");
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [isLoadingDetails, setIsLoadingDetails] = useState(false);
	const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
	const [isLoadingBatchShipments, setIsLoadingBatchShipments] = useState(false);
	const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);
	const [isLoadingPinkSheetData, setIsLoadingPinkSheetData] = useState(false);
	const [batchDetailsIdBuffer, setBatchDetailsIdBuffer] = useState<number>(0);
	const [batchDetails, setBatchDetails] = useState<IBatch>({
		id: batchDetailsIdBuffer,
		invoiceNumber: "",
		month: "-1",
		payeeId: "",
		payeeType: "",
		status: "Reconciling",
		shipments: []
	});
	const [batchShipments, setBatchShipments] = useState<IShipmentLight[]>([]);
	const [shipmentMaterials, setShipmentMaterials] = useState<IShipmentMaterial[]>([]);
	const [pinkSheetData, setPinkSheetData] = useState<IPinkSheetData[]>([]);
	const [payeeCache, setPayeeCache] = useState<IBatchPayeeOption[]>([]);
	const [documents, setDocuments] = useState<IDocument[]>([]);
	const [selectedDocumentId, setSelectedDocumentId] = useState<string>(undefined);
	const [isBatchShipmentsFormProcessing, setIsBatchShipmentsFormProcessing] = useState(false);
	const [shouldDisableWorkflowInputs, setShouldDisableWorkflowInputs] = useState(false);
	const [selectedPinkSheetShipmentId, setSelectedPinkSheetShipmentId] = useState<number>(-1);
	const [shipmentManifestWarningSets, setShipmentManifestWarningSets] = useState<IShipmentManifestWarningSet | undefined>(undefined);
	const [isLoadingShipmentManifestWarningSets, setIsLoadingShipmentManifestWarningSets] = useState(false);
	const [showManifestWarningSets, setShowManifestWarningSets] = useState(false);
	const [manifestWarningsTotal, setManifestWarningsTotal] = useState(0);

	useEffect(() => {
		getBatch();
		getShipmentManifestWarningSets();
		getDocuments();

		let et = listContext.entityTypes.filter((item) => { return item.name.toLowerCase() == "batch" })
		if (et.length > 0) {
			setEntityTypeId(et[0].value);
		}
	}, [id])

	// Put conditions where the workflow inputs should be disabled here
	useEffect(() => {
		if (isBatchShipmentsFormProcessing || (batchDetails?.status == "Reconciling" && (isLoadingShipmentManifestWarningSets || shipmentManifestWarningSets?.hasWarnings))) {
			setShouldDisableWorkflowInputs(true);
		} else {
			setShouldDisableWorkflowInputs(false);
		}
	}, [isBatchShipmentsFormProcessing, isLoadingShipmentManifestWarningSets, shipmentManifestWarningSets, batchDetails]);

	useEffect(() => {
		if (batchDetails?.id && batchDetails.id != batchDetailsIdBuffer) {
			setBatchDetailsIdBuffer(batchDetails.id);
			getBatchShipments();
		}
	}, [batchDetails])

	useEffect(() => {
		if (batchShipments.length > 0) {
			getAllMaterialsForBatch();
			getAllPinkSheetDataForBatch();
		}
	}, [batchShipments])

	useEffect(() => {
		if (shipmentManifestWarningSets) {
			setManifestWarningsTotal(
				shipmentManifestWarningSets?.hazmatViolations?.length ?? 0
			);
		}
	}, [shipmentManifestWarningSets])

	const getBatch = async () => {
		setIsLoadingDetails(true);

		BatchService.getBatch(id)
			.then((response) => {
				console.log('BatchService.getBatch: SUCCESS', response);
				setBatchDetails(BatchService.mapBatch(response.data));
			})
			.catch((error) => {
				console.log('BatchService.getBatch: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsLoadingDetails(false);
				setIsInitialLoad(false);
			});
	}

	const getBatchShipments = () => {
		setIsLoadingBatchShipments(true);

		BatchService.getBatchShipments(batchDetails.id.toString())
			.then((response) => {
				console.log('BatchService.getBatchShipments: SUCCESS', response);
				const shipments = ShipmentService.mapShipmentLights(response.data);
				shipments.sort((a, b) => UtilityService.compareStrings(a.hwiNumber, b.hwiNumber));
				setBatchShipments(shipments);
			})
			.catch((error) => {
				console.log('BatchService.getBatchShipments: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsLoadingBatchShipments(false);
			})
	}

	const getAllMaterialsForBatch = (): IShipmentMaterial[] => {
		const ids = getShipmentIdsFromBatch();
		if (ids.length == 0) return [];
		setIsLoadingMaterials(true);

		ShipmentService.getMultipleShipmentsMaterials(ids)
			.then((response) => {
				console.log('ShipmentService.getMultipleShipmentsMaterials: SUCCESS', response);
				const newMaterials = ShipmentService.mapShipmentMaterials(response.data);
				setShipmentMaterials(newMaterials);
			})
			.catch((error) => {
				console.log('ShipmentService.getMultipleShipmentsMaterials: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsLoadingMaterials(false);
			});
	}

	const getAllPinkSheetDataForBatch = () => {
		const ids = getShipmentIdsFromBatch();
		if (ids.length == 0) return;
		setIsLoadingPinkSheetData(true);

		ShipmentService.getPinkSheetData(ids)
			.then((response) => {
				console.log('ShipmentService.getMultipleShipmentsPinkSheetData: SUCCESS', response);
				setPinkSheetData(response.data);
			})
			.catch((error) => {
				console.log('ShipmentService.getMultipleShipmentsPinkSheetData: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsLoadingPinkSheetData(false);
			});
	}

	const getShipmentIdsFromBatch = (): number[] => {
		return batchShipments ? batchShipments.map(s => s.id) : [];
	}

	const getDocuments = async () => {
		if (id) {
			setIsLoadingDocuments(true);
			BatchService.getBatchDocuments(id)
				.then((response) => {
					if (response?.data) {
						console.log('BatchService.getBatchDocuments: SUCCESS', response);
						if (selectedDocumentId && !response.data.find(doc => doc.id.toString() == selectedDocumentId)) {
							setSelectedDocumentId(undefined);
						}
						setDocuments(DocumentService.mapDocuments(response.data));
					}
					else {
						throw new Error(`The response did not contain data: [${response.status}] ${response.statusText}`);
					}
				})
				.catch((error) => {
					console.log('BatchService.getBatchDocuments: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsLoadingDocuments(false);
				});
		}
	}

	const getShipmentManifestWarningSets = () => {
		setIsLoadingShipmentManifestWarningSets(true);

		BatchService.getShipmentManifestWarningSetsByBatchId(+id)
			.then((response) => {
				console.log('BatchService.getShipmentManifestWarningSetsByBatchId: SUCCESS', response);
				setShipmentManifestWarningSets(response.data);
			})
			.catch((error) => {
				console.log('BatchService.getShipmentManifestWarningSetsByBatchId: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsLoadingShipmentManifestWarningSets(false);
			});
	}

	const getShipmentCustomerName = (shipmentId: number): string => {
		const shipment = batchShipments?.find(s => s.id === shipmentId);
		return shipment?.customerName ?? "Unknown Customer";
	}

	const getShipmentVendorName = (shipmentId: number): string => {
		const shipment = batchShipments?.find(s => s.id === shipmentId);
		return shipment?.vendorName ?? "Unknown Vendor";
	}

	const updatePayeeCache = (payees: IBatchPayeeOption[]) => {
		setPayeeCache(payees);
	}

	const updateBatchDetails = (details: IBatch) => {
		setBatchDetails(details);
	};

	const handleBatchStatusUpdate = (status: string) => {
		setBatchDetails({ ...batchDetails, status: status });
	}

	const handleDocumentDeleted = (id: number) => {
		setDocuments(documents.filter(doc => doc.id !== id));
	}

	const handleOnBatchShipmentsUpdate = (shipments: IShipmentLight[]) => {
		if (shipments?.length > 0) {
			getShipmentManifestWarningSets();
		}
		else {
			setShipmentManifestWarningSets(undefined);
		}
		setBatchShipments(shipments);
	}

	return (
		<div className="hwi-batch-edit">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canReadBatches || userContext.permissions?.canManageBatches}>
					<Spinner dependency={isInitialLoad && isLoadingDetails} text='Loading batch...'>
						{(id) &&
							<>
								<section className="management-page-header">
									<div className="title">
										<NavLink to="/batch">
											<Icons.GoBack fill="#9399ad" height="24px"></Icons.GoBack>
										</NavLink>
										Edit Batch
									</div>
									<div className="tabs">
										<div className={activeTab == "details" ? "tab active" : "tab"} onClick={() => { setActiveTab("details"); }}>Details</div>
										<div className={activeTab == "shipments" ? "tab active" : "tab"} onClick={() => setActiveTab("shipments")}>Shipments</div>
										<div className={activeTab == "history" ? "tab active" : "tab"} onClick={() => setActiveTab("history")}>History</div>
										<div className={activeTab == "report" ? "tab active" : "tab"} onClick={() => setActiveTab("report")}>Report</div>
										<div className={activeTab == "documents" ? "tab active" : "tab"} onClick={() => { setActiveTab("documents"); }}>Documents</div>
									</div>
								</section>
								<section>
									<BatchWorkflowForm
										batchId={id}
										onBatchStatusUpdate={handleBatchStatusUpdate}
										areInputsDisabled={shouldDisableWorkflowInputs}
										isLoadingBatchShipments={isLoadingBatchShipments}
										batchShipments={batchShipments}
									></BatchWorkflowForm>
								</section>
								{errorMessage &&
									<Alert className="alert-error"
										severity="error"
										action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
										{errorMessage}
									</Alert>
								}
								{(batchDetails && payeeCache?.length > 0 && (isLoadingShipmentManifestWarningSets || shipmentManifestWarningSets?.hasWarnings)) &&
									<div className="alert-manifest-warnings">
										<Alert className={isLoadingShipmentManifestWarningSets ? "alert-info" : "alert-error"}
											severity={isLoadingShipmentManifestWarningSets ? "info" : "error"}
											action={
												!isLoadingShipmentManifestWarningSets &&
												<Button variant="text" color='error' onClick={() => setShowManifestWarningSets(!showManifestWarningSets)}>{showManifestWarningSets ? "Hide List" : "Show List"}</Button>
											}>
											{isLoadingShipmentManifestWarningSets ? "Shipment Manifests" : showManifestWarningSets ? <div className="manifest-warnings-header">Manifest Warnings</div> : <>This shipment has <strong>{manifestWarningsTotal} manifest warning{manifestWarningsTotal == 1 ? "" : "s"}</strong>.</>}
											<Spinner dependency={isLoadingShipmentManifestWarningSets} text="Validating shipment manifests...">
												{showManifestWarningSets && shipmentManifestWarningSets?.hazmatViolations?.map((violation, index) => (
													<div className="warning-set" key={index}>
														<div className="warning-set-header">
															<div className="bol">
																<span className="open-shipment">
																	<a href={`/shipment/${violation.shipmentId}`} target="_blank" rel="opener"><Icons.ViewItem fill="#272f50" height="14px"></Icons.ViewItem> {violation.hwiNumber}</a>
																</span>
															</div>
															<span className="customer-vendor">
																{getShipmentCustomerName(violation.shipmentId)}&nbsp;&nbsp;&#8226;&nbsp;&nbsp;{getShipmentVendorName(violation.shipmentId)}
															</span>
														</div>
														<ul>
															<li key={index}>{<div>There {violation.unassigned === 1 ? "is" : "are"} <strong>{violation.unassigned}</strong> hazardous material{violation.unassigned === 1 ? "" : "s"} not assigned a manifest.</div>}</li>
														</ul>
													</div>
												))}
											</Spinner>
										</Alert>
									</div>
								}
								{activeTab == "details" &&
									<section>
										<BatchDetailsForm
											batchDetails={batchDetails}
											updateBatchDetails={updateBatchDetails}
											isLoadingDetails={isLoadingDetails}
											payeeCache={payeeCache}
											updatePayeeCache={updatePayeeCache}
										></BatchDetailsForm>
									</section>
								}
								{activeTab == "shipments" &&
									<section>
										<BatchShipmentsForm
											batch={batchDetails}
											shipments={batchShipments}
											isLoadingShipments={isLoadingBatchShipments}
											onIsProcessingChange={(isProcesssing) => setIsBatchShipmentsFormProcessing(isProcesssing)}
											onBatchShipmentsUpdate={handleOnBatchShipmentsUpdate}
										></BatchShipmentsForm>
									</section>
								}
								{activeTab == "documents" &&
									<section>
										<Spinner dependency={isLoadingDocuments} text="Loading documents...">
											{errorMessage &&
												<Alert className="alert-error" severity="error" action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>{errorMessage}</Alert>
											}
											<DocumentTable
												data={documents}
												entityId={id}
												entityTypeId={entityTypeId}
												hideAddDocumentsLink={!userContext.permissions?.canManageBatches}
												hideDeleteDocumentsLink={!userContext.permissions?.canManageBatches || batchDetails?.status === "Complete"}
												onViewDocument={(id) => { setSelectedDocumentId(id?.toString()) }}
												onRequestListRefresh={getDocuments}
												onDocumentDeleted={(id) => { handleDocumentDeleted(id) }}
											></DocumentTable>
											<div className="document-viewer-container">
												{selectedDocumentId ? (
													<DocumentViewer documentId={+selectedDocumentId}></DocumentViewer>
												) : (
													documents.length > 0 && (
														<Alert className="alert" severity="info">
															Select a document to view its contents.
														</Alert>
													)
												)}
											</div>
										</Spinner>
										<br />
										<Spinner dependency={isLoadingBatchShipments || isLoadingMaterials || isLoadingPinkSheetData} text="Loading Pink Sheets...">
											<div className="pink-sheet-container">
												<div className="pink-sheet-header">
													<div className="title">
														Pink Sheets ({batchShipments?.length ?? 0})
													</div>
													<div className="controls">
														<FormControl size="small">
															<InputLabel>Viewing</InputLabel>
															<Select
																className="pink-sheet-selector"
																value={selectedPinkSheetShipmentId}
																label="Viewing"
																fullWidth
																onChange={(event) => { setSelectedPinkSheetShipmentId(+event.target.value) }}
															>
																<MenuItem value="-1" key={-1} className="pink-sheet-item">All Shipments</MenuItem>
																{batchShipments.map((shipment, index) => (
																	<MenuItem
																		value={shipment.id}
																		key={index}
																		className="pink-sheet-item"
																	>
																		<span className="index-number">
																			{(index + 1).toString().padStart(Math.ceil(Math.log10(batchShipments.length) + 1), ' ')}:&nbsp;
																		</span>
																		<strong>
																			{shipment.hwiNumber}
																		</strong>
																		<span>
																			&nbsp;
																			({shipment.customerName}&nbsp;&#8212;&nbsp;{shipment.vendorName})
																		</span>
																	</MenuItem>
																))}
															</Select>
														</FormControl>
													</div>
												</div>
													<div className="pink-sheet-wrapper">
														{batchShipments?.length > 0 ? (
															batchShipments.filter(bs => selectedPinkSheetShipmentId === -1 || bs.id === selectedPinkSheetShipmentId).map((bs, index) => (
																<PinkSheet
																	key={index}
																	shipment={batchShipments.find(s => s.id === bs.id)}
																	materials={shipmentMaterials.filter(m => m.shipmentId === bs.id)}
																	isLoadingMaterials={isLoadingMaterials}
																	pinkSheetData={pinkSheetData.find(p => p.shipment.id === bs.id)}
																	isLoadingPinkSheetData={isLoadingPinkSheetData}
																	showHwiNumberInHeader={selectedPinkSheetShipmentId === -1}
																	pageNumber={(index + 1).toString()}
																	pagesTotal={batchShipments.length.toString()}
																></PinkSheet>
															))
														) : (
															<Alert className="alert" severity="info">
																Pink Sheets will appear here as shipments are added to the batch.
															</Alert>
														)}
													</div>
											</div>
										</Spinner>
									</section>
								}
								{activeTab == "history" &&
									<section>
										<BatchHistoryForm id={id} status={batchDetails.status}></BatchHistoryForm>
									</section>
								}
								{activeTab == "report" &&
									<section>
										<PowerBiReportViewer reportId="e5c53f90-d053-4882-b52a-ad84ba7728de" parameters={{ "rp:BatchNumber": id }} showNav={false}></PowerBiReportViewer>
									</section>
								}
							</>
						}
					</Spinner>
				</PermissionGate>
			</Container>
			{/* <Json data={batchShipments}></Json> */}
		</div >
	);
};

export default BatchEdit;